import { useCallback } from 'react';

import {
  useCreateWhiteboardForLessonMutation,
  useLinkWhiteboardToLessonMutation,
  useRenameBoardMutation,
  useSaveBoardSceneMutation,
  useUnlinkWhiteboardFromLessonMutation,
  useUploadBoardImageMutation,
} from 'core/graphql';

/** Whiteboard error codes — plain strings by contract (spec 0010 §4). */
export enum WhiteboardErrorCode {
  SCENE_TOO_LARGE = 'SCENE_TOO_LARGE',
  INVALID_SCENE = 'INVALID_SCENE',
  LIBRARY_TOO_LARGE = 'LIBRARY_TOO_LARGE',
  INVALID_LIBRARY = 'INVALID_LIBRARY',
  VERSION_CONFLICT = 'VERSION_CONFLICT',
  LESSON_ALREADY_HAS_BOARD = 'LESSON_ALREADY_HAS_BOARD',
  BOARD_NOT_IN_GROUP = 'BOARD_NOT_IN_GROUP',
  NOT_LESSON_TEACHER = 'NOT_LESSON_TEACHER',
  /** Per-image S3 cap is 12MB (spec 0010 rev.3). */
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  INVALID_FILE = 'INVALID_FILE',
}

export interface IWhiteboardMutationResult {
  success: boolean;
  errorCode: string | null;
}

export interface ISaveSceneResult extends IWhiteboardMutationResult {
  /**
   * New version on success; the server's CURRENT version on VERSION_CONFLICT
   * (the editor resends with it when the teacher chooses "overwrite").
   */
  sceneVersion: number | null;
}

/** Network errors propagate as thrown ApolloError — callers handle retry UX. */

export const useCreateWhiteboard = () => {
  const [mutate, { loading }] = useCreateWhiteboardForLessonMutation();

  const createWhiteboard = useCallback(
    async (lessonId: string, name?: string): Promise<IWhiteboardMutationResult> => {
      const { data } = await mutate({ variables: { lessonId, name } });
      const payload = data?.createLessonWhiteboard;
      return {
        success: Boolean(payload?.success),
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [mutate],
  );

  return { createWhiteboard, loading };
};

export const useLinkWhiteboard = () => {
  const [mutate, { loading }] = useLinkWhiteboardToLessonMutation();

  const linkWhiteboard = useCallback(
    async (lessonId: string, whiteboardId: string): Promise<IWhiteboardMutationResult> => {
      const { data } = await mutate({ variables: { lessonId, whiteboardId } });
      const payload = data?.linkLessonWhiteboard;
      return {
        success: Boolean(payload?.success),
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [mutate],
  );

  return { linkWhiteboard, loading };
};

export const useSaveWhiteboardScene = () => {
  const [mutate, { loading }] = useSaveBoardSceneMutation();

  const saveScene = useCallback(
    async (
      whiteboardId: string,
      sceneJson: string,
      expectedVersion: number,
    ): Promise<ISaveSceneResult> => {
      const { data } = await mutate({ variables: { whiteboardId, sceneJson, expectedVersion } });
      const payload = data?.saveWhiteboardScene;
      return {
        success: Boolean(payload?.success),
        errorCode: payload?.customErrors?.[0]?.code ?? null,
        sceneVersion: payload?.sceneVersion ?? null,
      };
    },
    [mutate],
  );

  return { saveScene, loading };
};

/**
 * Pushes ONE pasted scene image to S3 (spec 0010 rev.3). Multipart goes over
 * apollo-upload-client's createUploadLink, already wired into the client.
 *
 * Deliberately NOT refetching the board query: the caller already holds the
 * dataURL in the canvas, so re-reading the presigned url would only cost a
 * round trip. Network errors throw — the caller decides retry.
 */
export const useUploadWhiteboardFile = () => {
  const [mutate, { loading }] = useUploadBoardImageMutation();

  const uploadFile = useCallback(
    async (
      whiteboardId: string,
      fileId: string,
      file: File,
      mimeType?: string | null,
    ): Promise<IWhiteboardMutationResult> => {
      const { data } = await mutate({ variables: { whiteboardId, fileId, mimeType, file } });
      const payload = data?.uploadWhiteboardFile;
      return {
        success: Boolean(payload?.success),
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [mutate],
  );

  return { uploadFile, loading };
};

export const useRenameWhiteboard = () => {
  const [mutate, { loading }] = useRenameBoardMutation();

  const renameWhiteboard = useCallback(
    async (whiteboardId: string, name: string): Promise<IWhiteboardMutationResult> => {
      const { data } = await mutate({ variables: { whiteboardId, name } });
      const payload = data?.renameWhiteboard;
      return {
        success: Boolean(payload?.success),
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [mutate],
  );

  return { renameWhiteboard, loading };
};

export const useUnlinkWhiteboard = () => {
  const [mutate, { loading }] = useUnlinkWhiteboardFromLessonMutation();

  const unlinkWhiteboard = useCallback(
    async (lessonId: string): Promise<IWhiteboardMutationResult> => {
      const { data } = await mutate({ variables: { lessonId } });
      const payload = data?.unlinkLessonWhiteboard;
      return {
        success: Boolean(payload?.success),
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [mutate],
  );

  return { unlinkWhiteboard, loading };
};
